import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { SearchBtnIcon } from '@/components/agent-dashboard/assets';
import { TextInput } from '@/components/common/text-input';
import { CreateAudioButton } from '../common/create-audio-button';
import { VoiceCard } from './voice-card';
import { useUserManagementHooks } from '@/manager';
import { RadioButton } from '../common/radio-button';
import { LoadingOverlay } from '../common/loading-overlay';

interface AddVoiceModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  selectedId?: number;
  voices?: Audio[];
  loading?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (voiceIds: string[]) => Promise<void>;
}

/** 添加声音弹窗 */
export const AddVoiceModal: React.FC<AddVoiceModalProps> = ({
  trigger,
  open: externalOpen,
  selectedId,
  voices,
  loading,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const { renameVoice } = useUserManagementHooks.useVoiceRename();
  const {
    inputText,
    loading: searchLoading,
    searchVoice,
    changeValue
  } = useUserManagementHooks.useSearchVoice();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref

  // 全选状态
  const [selectAll, setSelectAll] = useState(false);

  // 选中的声音列表
  const [selectedVoiceList, setSelectedVoiceList] = useState<string[]>([]);

  // 处理单个声音卡片的选择变化
  const handleVoiceSelect = (id: string, isSelected: boolean) => {
    setSelectedVoiceList(prev => {
      if (isSelected) {
        // 如果被选中且不存在，则添加到列表
        if (!prev.includes(id)) {
          return [...prev, id];
        }
      } else {
        // 如果被取消选中，则从列表中移除
        return prev.filter(voiceId => voiceId !== id);
      }
      return prev;
    });
  };

  // 处理全选/取消全选
  useEffect(() => {
    if (selectAll && voices) {
      // 全选
      const allVoiceIds = voices.map(voice => voice.id);
      setSelectedVoiceList(allVoiceIds);
    } else if (!selectAll) {
      // 取消全选
      setSelectedVoiceList([]);
    }
  }, [selectAll, voices]);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (!open) {
      reset();
      changeValue('');
      searchVoice('');
    }
  };

  const handleRename = async (voiceId: string, name: string) => {
    await renameVoice({ voiceId, name });
  }

  const handleSave = async () => {
    await onSave?.(selectedVoiceList);
    handleClose();
  }

  const handleSearch = async () => {
    try {
      await searchVoice();
      reset();
    } catch (error) {
      console.error("Failed to search voices:", error);
    }
  }

  const reset = () => {
    setSelectAll(false);
    setSelectedVoiceList([]);
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };


  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[900px] h-[755px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6  border-b border-[rgba(233,234,235,1)] pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{t('agent_dashboard.user_detail.voice.add_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 pt-5 select-none mb-5 overflow-hidden'>
          <div className='flex items-center gap-4 mb-5'>
            <TextInput
              type="text"
              placeholder={t('agent_dashboard.user_detail.voice.add_modal.search_placeholder')}
              value={inputText}
              className='h-10'
              onChange={(e) => changeValue(e.target.value)}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.voice.add_modal.search')}
              icon={<SearchBtnIcon className='w-5 h-5' />}
              className={cn(
                '!w-[106px] h-9 ',
                'text-white font-semibold text-sm',
              )}
              onClick={handleSearch}
            />
          </div>
          <div className='relative pt-0.5 pl-0.5 pr-0.5 flex-1 flex flex-col h-full overflow-y-auto'>
            <div className='grid grid-cols-3 gap-x-3 gap-y-5'>
              <CreateAudioButton generateType="Minimax" />
              {voices.map((item, index) => (
                <VoiceCard
                  showDelete={false}
                  key={`${item.id}_${index}`}
                  name={item.name}
                  audioUrl={item.audioUrl}
                  id={item.id}
                  enableSelect
                  isSelected={selectedVoiceList.includes(item.id)}
                  onSelect={handleVoiceSelect}
                  onRename={(name) => handleRename(item.id, name)}
                />
              ))}
            </div>
            <LoadingOverlay loading={searchLoading} />
          </div>
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex items-center justify-between p-6 border-t border-[rgba(233,234,235,1)] z-20 select-none'>
          <div className='flex items-center gap-2'>
            <RadioButton
              label={t('agent_dashboard.user_detail.voice.add_modal.all_selecting')}
              checked={selectAll}
              onChange={setSelectAll}
            />
            <div className='text-xs text-[rgba(71,84,103,1)]'>
              {t('agent_dashboard.user_detail.voice.add_modal.selected')} {selectedVoiceList.length}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <TextButton
              label={t('agent_dashboard.user_detail.voice.add_modal.cancel')}
              className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
              onClick={handleClose}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.voice.add_modal.save')}
              className='w-[135px] h-11'
              onClick={handleSave}
              loading={loading}
              disabled={loading}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
